import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { AsyncDataTable, DataTableColumn, TableActions } from '@saucelabs/ds-table';
import { records as appStorageRecords, constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';
import { IconDependencyOutline } from '@saucelabs/ds-icons';
import { ModalContext } from '@saucelabs/ds-modal';

import { MANUAL_TEST_EVENTS, trackEvent } from '../../../tracking';
import constants from '../constants';
import selectors from '../selectors';
import actions from '../actions';
import operations from '../operations';

type RowData = {
  build: string;
  description: string;
  file: appStorageRecords.AppStorageFile;
  id: string;
  isInUse: boolean;
  name: string;
  uploaded: Date;
  version: string;
};

const FilesList: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    currentDevice,
    currentFile,
    files,
  } = useSelector(selectors.filesModalSelector);
  const {
    currentPage,
    totalItems,
  } = useSelector(selectors.filesListPaginationSelector);
  const { hideModal } = useContext(ModalContext); // eslint-disable-line @typescript-eslint/unbound-method

  const handlePagination = useCallback((newPage: number) => {
    void dispatch(actions.fetchAppStorageFiles({
      paginationPage: newPage,
    }));
  }, [dispatch]);

  const installAppVersion = useCallback((file: appStorageRecords.AppStorageFile) => {
    void dispatch(operations.installMobileAppDependency(file));

    hideModal?.();

    trackEvent(MANUAL_TEST_EVENTS.INSTALL_DEPENDENT_APP, {
      deviceName: currentDevice?.name,
      os: currentDevice?.os,
    });
  }, [currentDevice, hideModal, dispatch]);

  const columns: DataTableColumn<RowData>[] = useMemo(() => (
    [
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>{value as string}</div>
        )),
        field: 'name',
        key: 'name',
        name: 'File Name',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>{value as string}</div>
        )),
        field: 'id',
        key: 'id',
        name: 'File ID',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>{value as string}</div>
        )),
        field: 'description',
        hiddenBreakpoints: ['xs', 'sm', 'md'],
        key: 'description',
        name: 'Description',
        widthClass: 'w-24',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>{value as string}</div>
        )),
        field: 'version',
        helpText: (() => {
          if (currentFile.kind) {
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleShortVersionString" in Info.plist';
            }
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionName" in the app manifest';
            }
          }
          return '';
        })(),
        key: 'version',
        name: 'Version',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>{value as string}</div>
        )),
        name: 'Build',
        field: 'build',
        key: 'build',
        helpText: (() => {
          if (currentFile.kind) {
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleVersion" in Info.plist';
            }
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionCode" in the app manifest';
            }
          }
          return '';
        })(),
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isInUse })}>
            {row.file.getCreationDatePrettyLabel()}
          </div>
        )),
        field: 'uploaded',
        helpText: `Uploaded files will expire after ${appStorageConstants.APP_STORAGE_LIFETIME_DAYS} days`,
        key: 'uploaded',
        name: 'Uploaded',
      },
      {
        allowsSorting: false,
        customBodyRender: (value, key, row) => {
          if (row.isInUse) {
            return <div className="text-right text-disabled-emphasis">IN USE</div>;
          }
          return (
            <div className="w-9">
              <TableActions actions={[
                {
                  label: 'Install',
                  onPress: () => installAppVersion(row.file),
                  icon: IconDependencyOutline,
                  primaryAction: true,
                },
              ]}
              />
            </div>
          );
        },
        field: 'id',
        key: 'actions',
        name: '',
      },
    ]
  ), [currentFile, installAppVersion]);

  const rows = useMemo(
    () => (
      files.map((file) => ({
        build: file?.getBuild(),
        description: file?.description,
        file,
        id: file?.id,
        isInUse: file.group_id === currentFile.group_id,
        name: file?.name,
        uploaded: file?.getCreationDate(),
        version: file?.getVersion(),
      }))),
    [files, currentFile],
  );

  return (
    <AsyncDataTable
      aria-label="Install Dependent Apps Files"
      columns={columns}
      isPaginated
      itemsPerPage={constants.FETCH_FILES_PAGE_SIZE}
      itemsPerPageOptions={undefined}
      onPageNumberChange={handlePagination}
      pageNumber={currentPage}
      rows={rows}
      totalItemCount={totalItems}
    />
  );
};

FilesList.displayName = 'InstallDependentAppFilesList';

export default FilesList;
